import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import type { CampaignReporting } from 'c-entity-types';
import { useEntityData } from 'c-data';
import { Button, CollapsibleCard, FlashMessage, TransparentCard } from 'c-components';
import { Box, CardContent, Stack, TextField, Typography } from '@mui/material';
import { PermissionName } from 'c-sdk';
import { dashboardIcon } from 'c-main/icons';
import { useEntityPage } from 'c-hooks';
import { useUserPermissions } from 'c-auth-module/Hooks';
import ReactGA from 'react-ga';
import { useCampaignReportingConfigs } from 'c-main/Components/Campaign/useCampaignReportingConfigs';
import DashboardCustomizationDialog from 'c-dashboard-module/DashboardCustomizationDialog';
import type { DashboardConfig } from 'c-dashboard-module/dashboardConfig';
import { normalizeDashboardConfig } from 'c-dashboard-module/dashboardConfig';
import ConfigUserAssign from './Components/ConfigUserAssign';

const EditDashboardConfigPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    const { id } = useEntityPage();
    const { getById } = useEntityData<CampaignReporting>('CampaignReporting');
    const config = getById({ id });

    const { hasAll } = useUserPermissions();
    const canRename = hasAll([PermissionName.UniledportalCampaign_reportingUpdate]);

    // Renaming goes through the same save the customisation dialog uses, so the two can't
    // drift apart on what a valid update looks like.
    const { save, isSaving } = useCampaignReportingConfigs('dashboard');
    const [name, setName] = useState('');
    useEffect(() => {
        if (config?.name) setName(config.name);
    }, [config?.name]);

    const rename = useCallback(() => {
        const trimmed = name.trim();
        if (!trimmed || !config) return;
        save(trimmed, config.config, config.id);
    }, [config, name, save]);

    const canEdit = hasAll([PermissionName.UniledportalCampaign_reportingUpdate]);
    const [saved, setSaved] = useState<'ok' | 'error' | null>(null);

    // The stored config is a JSON string; a malformed one shouldn't take the page down.
    const parsed = useMemo<DashboardConfig | null>(() => {
        if (!config?.config) return null;
        try {
            return normalizeDashboardConfig(JSON.parse(config.config) as DashboardConfig);
        } catch {
            return null;
        }
    }, [config?.config]);

    const saveConfig = useCallback(
        async (next: DashboardConfig) => {
            if (!config) return;
            const ok = await save(config.name, JSON.stringify(next), config.id);
            setSaved(ok ? 'ok' : 'error');
        },
        [config, save],
    );

    return (
        <TransparentCard
            title={`Dashboard: ${config?.name ?? id}`}
            titleTypographyProps={{ sx: { fontSize: '1.9rem' } }}
            cardHeaderProps={{ sx: { py: 1 } }}
        >
            <CardContent>
                <Stack gap={3}>
                    {canRename && (
                        <Stack direction="row" gap={2} alignItems="center">
                            <TextField
                                size="small"
                                label="Name"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                sx={{ minWidth: 260 }}
                            />
                            <Button onClick={rename} disabled={!name.trim() || isSaving}>
                                Rename
                            </Button>
                        </Stack>
                    )}

                    <Typography variant="body2" color="text.secondary">
                        Anyone assigned below sees this dashboard on their Custom tab. Assignments
                        made against an agency, buyer, brand or media owner reach every user within
                        it.
                    </Typography>

                    <ConfigUserAssign
                        title="Modules.Admin.DashboardSetup.assignTitle"
                        configId={id}
                    />

                    {canEdit && (
                        <CollapsibleCard title="Modules.Admin.DashboardSetup.editorTitle">
                            <Box sx={{ p: 2 }}>
                                {saved === 'ok' && (
                                    <FlashMessage status="success">Dashboard saved.</FlashMessage>
                                )}
                                {saved === 'error' && (
                                    <FlashMessage status="error">
                                        Could not save this dashboard.
                                    </FlashMessage>
                                )}
                                {parsed == null ? (
                                    <FlashMessage status="error">
                                        This saved dashboard could not be read, so it cannot be
                                        edited here.
                                    </FlashMessage>
                                ) : (
                                    <DashboardCustomizationDialog
                                        inline
                                        value={parsed}
                                        onSave={saveConfig}
                                    />
                                )}
                            </Box>
                        </CollapsibleCard>
                    )}
                </Stack>
            </CardContent>
        </TransparentCard>
    );
};

const setup: PageEntityConfig<CampaignReporting> = {
    id: 'EditDashboardConfigPage',
    component: EditDashboardConfigPage,
    systemSearch: {
        title: 'Modules.Admin.DashboardSetup.editMetaTitle',
        description: 'Modules.Admin.DashboardSetup.description',
        type: 'entity',
        icon: dashboardIcon,
        route: PostAuthRoutes.Admin.EditDashboardConfig,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.Admin.DashboardSetup.metaTitle',
                link: PostAuthRoutes.Admin.DashboardSetup,
            },
        ],
        defaultIncludes: ['agencies', 'buyers', 'clients', 'owners', 'users'],
        entityName: 'CampaignReporting',
        nameGen: (entity, id) => entity?.name ?? String(id),
        permissions: [
            PermissionName.UniledportalCampaign_reportingRead,
            PermissionName.UniledportalCampaign_reportingAssign,
        ],
        prefix: 'SystemSearch.prefixes.dashboardConfig',
        searchColumn: ['name'],
        prefixDescription: 'SystemSearch.prefixDescriptions.dashboardConfig',
    },
};

export default setup;
